import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Check, Star, Info } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { buttonText } from "@/lib/buttonTexts";
import { ekkoText } from "@/lib/ekkoDefaults";
import { stripHtml } from "@/lib/text";

// Helper to strip HTML tags
const filled = (items) => (Array.isArray(items) ? items.filter((i) => stripHtml(i?.nome)) : []);
const formatPrice = (value) => (typeof value === "number" ? `${value} €` : stripHtml(value));

export default function PricingSection({ prices, loading, content = {} }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const pricingTitle = ekkoText(content, "pricing_title");
  const fee = (key) => Number(prices?.inscricao?.[key]) || 0;
  const hasFees = ["joia", "seguro_anual", "curso_iniciacao"].some((k) => fee(k) > 0);
  const pricingSubtitle = ekkoText(content, "pricing_subtitle");

  const scrollToContact = () => {
    const element = document.querySelector("#contacto");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="precos"
      ref={ref}
      className="py-24 md:py-32 bg-[#111111] relative"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F5F5F5] mb-4 tracking-wider rich-text-content"
            data-testid="pricing-title"
            dangerouslySetInnerHTML={{ __html: pricingTitle }}
          />
          <p 
            className="text-[#C9C9C9] text-base md:text-lg max-w-2xl mx-auto rich-text-content"
            data-testid="pricing-subtitle"
            dangerouslySetInnerHTML={{ __html: pricingSubtitle }}
          />
        </motion.div>

        {/* Mensalidades - FIRST */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-64 bg-[#1F1F1F]" />
            ))}
          </div>
        ) : (
          <>
{filled(prices?.mensalidades).length > 0 && (
            <>
            <motion.h3
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-2xl font-bold text-[#F5F5F5] mb-6 tracking-wider text-center"
            >
              PLANOS
            </motion.h3>
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
              data-testid="pricing-grid"
            >
              {filled(prices?.mensalidades).map((plan, index) => (
                <motion.div
                  key={stripHtml(plan.nome) || index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className={`relative bg-[#010000] border ${
                    plan.popular ? "border-[#A6C48A]" : "border-[#2A2A2A]"
                  } p-8 rounded-2xl group hover:border-[#A6C48A] transition-colors duration-500`}
                  data-testid={`pricing-card-${index}`}
                >
                  {plan.popular && (
                    <div className="popular-badge rounded-bl-lg" data-testid="popular-badge">
                      <Star size={12} className="inline mr-1" fill="white" />
                      MAIS POPULAR
                    </div>
                  )}

                  <div
                    className="text-xl font-bold text-[#F5F5F5] tracking-wider mb-2 rich-text-content"
                    dangerouslySetInnerHTML={{ __html: plan.nome }}
                  />
                  <div 
                    className="text-[#9A9A9A] text-sm mb-4 rich-text-content"
                    dangerouslySetInnerHTML={{ __html: plan.descricao }}
                  />

                  <div className="mb-6">
                    <span className="text-4xl font-bold text-[#A6C48A]">
                      {formatPrice(plan.preco)}
                    </span>
                    {plan.periodo && (
                      <span className="text-[#9A9A9A] text-sm ml-2">/{stripHtml(plan.periodo)}</span>
                    )}
                  </div>

                  <button
                    onClick={scrollToContact}
                    className={`w-full btn-skew py-3 font-bold uppercase tracking-wider text-sm ${
                      plan.popular
                        ? "bg-[#A6C48A] text-[#010000] hover:bg-[#B9D3A1]"
                        : "border border-[#A6C48A] text-[#A6C48A] hover:bg-[#A6C48A] hover:text-[#010000]"
                    } transition-colors`}
                    data-testid={`pricing-cta-${index}`}
                  >
                    <span>{buttonText(content, "pricing_cta")}</span>
                  </button>
                </motion.div>
              ))}
            </div>
            </>
            )}

            {hasFees && (
            <>
            {/* Inscrição Info - AFTER Mensalidades */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-[#010000]/50 border border-[#A6C48A]/30 p-6 mb-12 rounded-2xl"
              data-testid="inscription-info"
            >
              <div className="flex items-center gap-2 mb-4">
                <Info className="text-[#A6C48A]" size={20} />
                <span
                  className="text-lg font-bold text-[#F5F5F5] tracking-wider"
                >
                  INSCRIÇÃO
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                {fee("joia") > 0 && (
                  <div className="flex justify-between items-center p-3 bg-[#1F1F1F]/50 rounded-xl">
                    <span className="text-[#C9C9C9]">Inscrição</span>
                    <span className="text-[#A6C48A] font-bold">{fee("joia")} €</span>
                  </div>
                )}
                {fee("seguro_anual") > 0 && (
                  <div className="flex justify-between items-center p-3 bg-[#1F1F1F]/50 rounded-xl">
                    <span className="text-[#C9C9C9]">Seguro anual</span>
                    <span className="text-[#A6C48A] font-bold">{fee("seguro_anual")} €</span>
                  </div>
                )}
                {fee("curso_iniciacao") > 0 && (
                  <div className="flex justify-between items-center p-3 bg-[#1F1F1F]/50 rounded-xl">
                    <span className="text-[#C9C9C9]">Sessão de iniciação</span>
                    <span className="text-[#A6C48A] font-bold">{fee("curso_iniciacao")} €</span>
                  </div>
                )}
              </div>
            </motion.div>
            </>
            )}

            {filled(prices?.drop_in).length > 0 && (
            <>
            {/* Drop-in Packs */}
            <motion.h3
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-2xl font-bold text-[#F5F5F5] mb-6 tracking-wider text-center"
            >
              PACKS
            </motion.h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {filled(prices?.drop_in).map((item, index) => (
                <motion.div
                  key={stripHtml(item.nome) || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.05 }}
                  className="bg-[#010000] border border-[#2A2A2A] p-4 rounded-2xl text-center hover:border-[#A6C48A] transition-colors"
                  data-testid={`drop-in-${index}`}
                >
                  <div 
                    className="text-[#C9C9C9] text-sm mb-2 rich-text-content"
                    dangerouslySetInnerHTML={{ __html: item.nome }}
                  />
                  <p className="text-2xl font-bold text-[#A6C48A]">{formatPrice(item.preco)}</p>
                </motion.div>
              ))}
            </div>
            </>
            )}

            {filled(prices?.servicos_extra).length > 0 && (
            <>
            {/* Serviços Extra */}
            <motion.h3
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-2xl font-bold text-[#F5F5F5] mb-6 tracking-wider text-center"
            >
              SERVIÇOS
            </motion.h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filled(prices?.servicos_extra).map((item, index) => (
                <motion.div
                  key={stripHtml(item.nome) || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.03 }}
                  className="bg-[#010000] border border-[#2A2A2A] p-4 rounded-2xl flex justify-between items-center hover:border-[#A6C48A] transition-colors"
                  data-testid={`service-${index}`}
                >
                  <span 
                    className="text-[#C9C9C9] text-sm rich-text-content"
                    dangerouslySetInnerHTML={{ __html: item.nome }}
                  />
                  <span className="text-[#A6C48A] font-bold">
                    {formatPrice(item.preco)}
                  </span>
                </motion.div>
              ))}
            </div>
            </>
            )}
          </>
        )}
      </div>

      {/* Section Divider */}
      <div className="section-divider mt-24" />
    </section>
  );
}
